/**
 * uploads.js — tiny pub/sub + sequential queue for bulk receipt upload.
 *
 * Lives outside React so state survives modal unmount: the user can minimize
 * the Upload Receipts modal and continue working while files keep processing.
 *
 * Concurrency: **1**. Enforced by `_processing` guard. The next file only
 * starts after the previous completes/fails/skips.
 *
 * One file = { id, name, size, type, dataUrl, b64, status, receiptId?, error? }
 * status ∈ { 'queued', 'processing', 'done', 'failed', 'skipped' }
 */

import { extractReceiptFromImages, createReceipt, uploadReceiptImages } from './api';

const MAX_FILES = 10;
const MAX_FILE_BYTES = 15 * 1024 * 1024; // 15 MB
const PER_FILE_TIMEOUT_MS = 90_000;
export const MAX_PDF_PAGES = 1;
const IMAGE_TYPES = new Set(['image/jpeg', 'image/png']);
const PDF_TYPES = new Set(['application/pdf']);
const ALLOWED_TYPES = new Set([...IMAGE_TYPES, ...PDF_TYPES]);

const isPdf = (file) => PDF_TYPES.has(file.type) || /\.pdf$/i.test(file.name);

let _nextId = 1;
let _queue = [];                 // array of items in insertion order
let _processing = false;         // single-worker guard
let _active = null;              // id currently processing
let _subscribers = new Set();

const emit = () => {
  const snapshot = snapshotState();
  for (const fn of _subscribers) {
    try { fn(snapshot); } catch { /* subscriber errors must not break queue */ }
  }
};

export function subscribe(fn) {
  _subscribers.add(fn);
  fn(snapshotState());
  return () => _subscribers.delete(fn);
}

export function snapshotState() {
  const items = _queue.map(({ dataUrl, b64, pdfFile, rawFile, ...rest }) => ({
    ...rest,
    preview: dataUrl,
  }));
  const total = items.length;
  const done = items.filter((i) => i.status === 'done').length;
  const failed = items.filter((i) => i.status === 'failed').length;
  const skipped = items.filter((i) => i.status === 'skipped').length;
  const processing = items.find((i) => i.status === 'processing') || null;
  const queued = items.filter((i) => i.status === 'queued').length;
  const activeIndex = processing ? items.findIndex((i) => i.id === processing.id) : -1;
  const allSettled = total > 0 && (done + failed + skipped) === total;
  return { items, total, done, failed, skipped, queued, processing, activeIndex, running: _processing, allSettled };
}

export function clearQueue() {
  if (_processing) return false; // can't clear while running
  _queue = [];
  _active = null;
  emit();
  return true;
}

export function removeItem(id) {
  if (_active === id) return false; // can't remove the one actively processing
  _queue = _queue.filter((i) => i.id !== id);
  emit();
  return true;
}

/**
 * Add files to the queue. Returns `{ added, rejected }`.
 * `rejected` is an array of `{ name, reason }` entries for anything that
 * failed validation.
 */
export async function addFiles(fileList) {
  const added = [];
  const rejected = [];
  const files = Array.from(fileList || []);

  for (const file of files) {
    if (_queue.length + added.length >= MAX_FILES) {
      rejected.push({ name: file.name, reason: `Max ${MAX_FILES} files per upload` });
      continue;
    }
    if (!ALLOWED_TYPES.has(file.type) && !isPdf(file)) {
      rejected.push({ name: file.name, reason: `Unsupported type (${file.type || 'unknown'}). Use JPG, PNG or PDF.` });
      continue;
    }
    if (file.size > MAX_FILE_BYTES) {
      rejected.push({ name: file.name, reason: `Too large (${(file.size / 1024 / 1024).toFixed(1)} MB — max 15 MB)` });
      continue;
    }
    // Dedupe by name+size
    const dup = _queue.some((i) => i.name === file.name && i.size === file.size);
    if (dup || added.some((i) => i.name === file.name && i.size === file.size)) {
      rejected.push({ name: file.name, reason: 'Already in queue' });
      continue;
    }
    try {
      const pdf = isPdf(file);
      // PDFs: keep the raw file blob for lazy render-on-process; images: eagerly
      // read to base64 so the preview thumbnail is instant.
      let dataUrl = null, b64 = null, pdfFile = null;
      if (pdf) {
        pdfFile = file;
      } else {
        ({ dataUrl, b64 } = await readAsBase64(file));
      }
      added.push({
        id: String(_nextId++),
        name: file.name,
        size: file.size,
        type: file.type || (pdf ? 'application/pdf' : 'unknown'),
        isPdf: pdf,
        pdfFile,
        rawFile: file,          // keep the original File for Tigris upload
        dataUrl,
        b64,
        status: 'queued',
        error: null,
        receiptId: null,
        imageStored: false,
        imageWarn: null,
      });
    } catch (err) {
      rejected.push({ name: file.name, reason: 'Could not read file' });
    }
  }

  if (added.length > 0) {
    _queue = [..._queue, ...added];
    emit();
  }
  return { added: added.length, rejected };
}

/**
 * Start processing the queue sequentially. No-op if already running.
 * Returns a Promise that resolves when the queue is drained.
 */
export async function startProcessing({ onReceiptCreated } = {}) {
  if (_processing) return;
  _processing = true;
  emit();
  try {
    while (true) {
      const next = _queue.find((i) => i.status === 'queued');
      if (!next) break;
      await _processOne(next, { onReceiptCreated });
    }
  } finally {
    _processing = false;
    _active = null;
    emit();
  }
}

/**
 * Retry a single failed item.
 * If the worker is idle, this immediately starts processing it.
 * If the worker is busy, the retried item is marked queued and will be
 * picked up when the current one finishes.
 */
export async function retryItem(id, opts = {}) {
  const it = _queue.find((i) => i.id === id);
  if (!it || it.status !== 'failed') return false;
  it.status = 'queued';
  it.error = null;
  emit();
  if (!_processing) {
    // Kick the worker
    await startProcessing(opts);
  }
  return true;
}

export function skipItem(id) {
  const it = _queue.find((i) => i.id === id);
  if (!it) return false;
  if (_active === id) return false;
  if (it.status === 'done') return false;
  it.status = 'skipped';
  it.error = null;
  emit();
  return true;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------
async function _processOne(item, { onReceiptCreated }) {
  item.status = 'processing';
  _active = item.id;
  emit();

  // Step 1 — if this is a PDF, render first page → JPG base64.
  if (item.isPdf && !item.b64) {
    try {
      const { dataUrl, b64 } = await _renderPdfFirstPage(item.pdfFile);
      item.dataUrl = dataUrl;
      item.b64 = b64;
      item.pdfFile = null; // free the blob
      emit();
    } catch (err) {
      item.status = 'failed';
      item.error = 'Could not read PDF';
      _active = null;
      emit();
      return;
    }
  }

  let extracted = null;
  try {
    extracted = await withTimeout(
      extractReceiptFromImages([item.b64]),
      PER_FILE_TIMEOUT_MS,
      'OCR timed out',
    );
  } catch (err) {
    item.status = 'failed';
    item.error = humanize(err) || 'Extraction failed';
    _active = null;
    emit();
    return;
  }
  try {
    const payload = buildCreatePayload(extracted);
    const saved = await withTimeout(
      createReceipt(payload),
      PER_FILE_TIMEOUT_MS,
      'Save timed out',
    );
    item.status = 'done';
    item.receiptId = saved?.id || null;

    // Phase 26C+ — persist the actual image bytes to Tigris / disk so the
    // detail view has an image. If this fails we keep the row as Done but
    // surface a soft note; the receipt + extracted fields are already saved.
    if (item.receiptId && item.rawFile) {
      try {
        // Prefer the JPG we may have rendered from a PDF (`dataUrl`), else the
        // original File blob. Both end up as Blob with image/jpeg.
        let fileForUpload = item.rawFile;
        if (item.isPdf && item.dataUrl) {
          const resp = await fetch(item.dataUrl);
          const blob = await resp.blob();
          const base = (item.name || 'receipt').replace(/\.pdf$/i, '');
          fileForUpload = new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
        }
        await withTimeout(
          uploadReceiptImages(item.receiptId, [{ file: fileForUpload }]),
          PER_FILE_TIMEOUT_MS,
          'Image upload timed out',
        );
        item.imageStored = true;
        item.imageWarn = null;
      } catch (imgErr) {
        item.imageStored = false;
        item.imageWarn = 'Image not stored';
      } finally {
        // Free the blob to avoid memory bloat in long queues.
        item.rawFile = null;
      }
    }
    onReceiptCreated?.(saved);
  } catch (err) {
    item.status = 'failed';
    item.error = humanize(err) || 'Could not save receipt';
  }
  _active = null;
  emit();
}


// ---------------------------------------------------------------------------
// PDF → first-page JPG (dynamic import so the ~1MB pdfjs bundle only loads
// when actually needed).
// ---------------------------------------------------------------------------
async function _renderPdfFirstPage(file) {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
  try {
    // Worker: use the bundled worker URL via Vite/webpack-friendly import.
    const worker = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url').catch(() => null);
    if (worker?.default) pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  } catch { /* worker optional — pdfjs falls back to main thread */ }
  const buf = await file.arrayBuffer();
  const task = pdfjs.getDocument({ data: new Uint8Array(buf) });
  const doc = await task.promise;
  if (doc.numPages < 1) throw new Error('empty_pdf');
  const page = await doc.getPage(1);
  const viewport = page.getViewport({ scale: 2.0 });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport, canvas }).promise;
  const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
  const b64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  try { await doc.cleanup(); await doc.destroy(); } catch { /* noop */ }
  return { dataUrl, b64 };
}

function withTimeout(promise, ms, msg) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(msg)), ms);
    promise.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); },
    );
  });
}

function humanize(err) {
  if (!err) return null;
  if (typeof err === 'string') return err;
  if (err.message) return err.message;
  if (err.detail) {
    if (typeof err.detail === 'string') return err.detail;
    if (err.detail.message) return err.detail.message;
  }
  try { return JSON.stringify(err).slice(0, 200); } catch { return 'Unknown error'; }
}

function buildCreatePayload(ex) {
  // Map `/api/receipts/extract` response → `/api/receipts` payload.
  // Required fields: vendor, date, subtotal, gst, qst, total, currency, num_images_used.
  const f = (v, fallback = 0) => (typeof v === 'number' ? v : (v?.value ?? fallback));
  const g = (v) => (v?.confidence ?? 0);
  return {
    vendor:   { value: ex?.vendor?.value   ?? 'Unknown vendor', confidence: g(ex?.vendor) },
    date:     { value: ex?.date?.value     ?? todayIso(),       confidence: g(ex?.date) },
    subtotal: { value: f(ex?.subtotal, 0),                      confidence: g(ex?.subtotal) },
    gst:      { value: f(ex?.gst, 0),                           confidence: g(ex?.gst) },
    qst:      { value: f(ex?.qst, 0),                           confidence: g(ex?.qst) },
    total:    { value: f(ex?.total, 0),                         confidence: g(ex?.total) },
    currency: ex?.currency || 'CAD',
    num_images_used: ex?.num_images_used || 1,
    source: 'gallery',
    notes: null,
    category: null,
  };
}

function todayIso() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('read error'));
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const comma = dataUrl.indexOf(',');
      const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
      resolve({ dataUrl, b64 });
    };
    reader.readAsDataURL(file);
  });
}

export const CONFIG = { MAX_FILES, MAX_FILE_BYTES, ALLOWED_TYPES, PER_FILE_TIMEOUT_MS };
